import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "./api";
import type { Customer, CustomerSummary, DashboardStats, ServerMeta, WorkspaceSnapshot } from "./types";
import { CustomerList } from "./components/CustomerList";
import { CustomerForm } from "./components/CustomerForm";
import { CustomerDetail } from "./components/CustomerDetail";
import { Dashboard } from "./components/Dashboard";
import { Banner } from "./components/Banner";
import { ServerStatusBar } from "./components/ServerStatusBar";
import { LoginGate } from "./components/LoginGate";
import { MaskedRevenue } from "./components/MaskedRevenue";

type MainView = "dashboard" | "customer";

export default function App() {
  const [authed, setAuthed] = useState<boolean | null>(null);
  const [meta, setMeta] = useState<ServerMeta | null>(null);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [customers, setCustomers] = useState<CustomerSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [mainView, setMainView] = useState<MainView>("dashboard");
  const [detail, setDetail] = useState<{ customer: Customer; workspace: WorkspaceSnapshot } | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  const refreshList = useCallback(async () => {
    try {
      const [data, s] = await Promise.all([api.listCustomers(), api.stats()]);
      setCustomers(data.customers);
      setStats(s);
    } catch (e: unknown) {
      setError((e as Error).message);
    }
  }, []);

  const refreshDetail = useCallback(async (id: string) => {
    try {
      const data = await api.getCustomer(id);
      setDetail(data);
    } catch (e: unknown) {
      setError((e as Error).message);
    }
  }, []);

  useEffect(() => {
    api.meta()
      .then((m) => {
        setMeta(m);
        setAuthed(true);
      })
      .catch((e: Error & { status?: number }) => {
        if (e.status === 401) {
          setAuthed(false);
        } else {
          setError(e.message);
          setAuthed(true);
        }
      });
    refreshList();
  }, [refreshList]);

  useEffect(() => {
    if (selectedId && mainView === "customer") {
      refreshDetail(selectedId);
    } else {
      setDetail(null);
    }
  }, [selectedId, mainView, refreshDetail]);

  const filtered = useMemo(() => {
    if (!query.trim()) return customers;
    const q = query.trim().toLowerCase();
    return customers.filter(
      (c) =>
        c.id.toLowerCase().includes(q) ||
        (c.name || "").toLowerCase().includes(q) ||
        (c.channel || "").toLowerCase().includes(q),
    );
  }, [customers, query]);

  const flash = useCallback((kind: "info" | "error", msg: string) => {
    if (kind === "info") setInfo(msg);
    else setError(msg);
    setTimeout(() => {
      setInfo(null);
      setError(null);
    }, 4000);
  }, []);

  const onCreate = async (body: Partial<Customer>) => {
    try {
      const c = await api.createCustomer(body);
      setShowCreate(false);
      await refreshList();
      setSelectedId(c.id);
      setMainView("customer");
      flash("info", `已新增客户：${c.name}`);
    } catch (e: unknown) {
      flash("error", (e as Error).message);
    }
  };

  const selectCustomer = (id: string) => {
    setSelectedId(id);
    setMainView("customer");
  };

  const showDashboard = () => {
    setMainView("dashboard");
  };

  if (authed === false) {
    return (
      <LoginGate
        onLoggedIn={() => {
          setAuthed(true);
          api.meta().then(setMeta).catch((e) => setError(e.message));
          refreshList();
        }}
      />
    );
  }

  if (authed === null) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-ink-500">
        加载中…
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-center justify-between border-b border-ink-200 bg-white px-6 py-3 shadow-sm">
        <div className="flex items-baseline gap-3">
          <h1 className="text-lg font-semibold tracking-tight">AVM License Server</h1>
          <span className="text-xs text-ink-500">客户档案 · 签发 · Prompt · 交流记录</span>
        </div>
        <ServerStatusBar meta={meta} />
      </header>

      {error && <Banner kind="error" onClose={() => setError(null)}>{error}</Banner>}
      {info && <Banner kind="info" onClose={() => setInfo(null)}>{info}</Banner>}

      <div className="grid min-h-0 flex-1 grid-cols-[minmax(0,320px)_1fr] overflow-hidden">
        <aside className="flex min-h-0 flex-col overflow-hidden border-r border-ink-200 bg-white">
          <div className="shrink-0 border-b border-ink-100 px-3 py-2">
            <button
              className={
                mainView === "dashboard"
                  ? "mb-2 w-full rounded-md bg-indigo-50 px-2 py-1.5 text-left text-sm font-medium text-indigo-700"
                  : "mb-2 w-full rounded-md px-2 py-1.5 text-left text-sm text-ink-600 hover:bg-ink-50"
              }
              onClick={showDashboard}
            >
              <span className="flex flex-wrap items-center gap-1.5">
                <span className="font-medium">仪表盘</span>
                {stats && (
                  <>
                    <span className="inline-flex shrink-0 items-center gap-1 rounded-md bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800 ring-1 ring-emerald-200/70">
                      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
                      <span className="tabular-nums">{stats.paid_customers}</span>
                      付费
                    </span>
                    <span className="inline-flex shrink-0 items-center gap-1 rounded-md bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800 ring-1 ring-amber-200/70">
                      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
                      <span className="tabular-nums">{stats.trial_customers}</span>
                      试用
                    </span>
                  </>
                )}
              </span>
            </button>
            <div className="flex items-center gap-2">
              <input
                className="input min-w-0"
                placeholder="搜索：id / 姓名 / 渠道"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              <button className="btn-primary shrink-0 whitespace-nowrap" onClick={() => setShowCreate(true)}>
                + 新建
              </button>
            </div>
          </div>
          <CustomerList
            items={filtered}
            selectedId={mainView === "customer" ? selectedId : null}
            onSelect={selectCustomer}
          />
          <div className="shrink-0 border-t border-ink-100 px-3 py-2 text-xs text-ink-500">
            共 {customers.length} 个客户
            {stats && (
              <>
                <span className="text-ink-300"> · </span>
                <MaskedRevenue amount={stats.revenue_cny} className="text-emerald-600" />
              </>
            )}
          </div>
        </aside>

        <main className="min-h-0 overflow-auto bg-ink-50 scrollbar-thin">
          {mainView === "dashboard" ? (
            <Dashboard stats={stats} customers={customers} onSelect={selectCustomer} />
          ) : detail ? (
            <CustomerDetail
              key={detail.customer.id}
              meta={meta}
              data={detail}
              onChanged={async () => {
                if (selectedId) await refreshDetail(selectedId);
                await refreshList();
              }}
              onDeleted={async () => {
                setSelectedId(null);
                setMainView("dashboard");
                await refreshList();
                flash("info", "客户已删除");
              }}
              flash={flash}
            />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-ink-500">加载中…</div>
          )}
        </main>
      </div>

      {showCreate && (
        <CustomerForm
          title="新建客户"
          contactKinds={meta?.contact_kinds ?? []}
          onClose={() => setShowCreate(false)}
          onSubmit={onCreate}
        />
      )}
    </div>
  );
}
